import React from 'react';
import { Sparkles, Heart, Briefcase, Droplets, BookOpen, User, Star, Clock } from 'lucide-react';
import { FREE_KUNDLI_TRANSLATIONS } from '../../../../languages/freeKundli';
import { getPlanetName } from '../../../../utils/planetNames';

interface PredictionsTabProps {
    kundliData: any;
    language: 'english' | 'hindi';
}

const PredictionsTab: React.FC<PredictionsTabProps> = ({ kundliData, language }) => {
    const langKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = FREE_KUNDLI_TRANSLATIONS[langKey];

    // Check if we have faladesh data
    if (!kundliData.faladesh) {
        return (
            <div className="text-center p-20 bg-white/[0.08] border border-white/20 rounded-[32px] font-sans">
                <Sparkles size={48} className="mx-auto mb-6 text-primary-400 opacity-20" />
                <p className="text-primary-200/40 text-lg">{t.predictionsGenerating}</p>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-in fade-in duration-500 font-sans">

            {/* Overall Life Prediction - Faladesh */}
            {kundliData.faladesh?.overall_life && (
                <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                    <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8">
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-primary-600 to-indigo-600 rounded-xl md:rounded-2xl flex items-center justify-center text-white shadow-lg shadow-primary-900/30 shrink-0">
                            <BookOpen size={20} className="md:w-7 md:h-7" />
                        </div>
                        <div>
                            <h2 className="text-xl md:text-3xl font-black text-white">{t.faladeshTitle}</h2>
                            <p className="text-primary-200/50 text-[11px] md:text-sm font-light italic mt-1 leading-tight">{t.faladeshDesc}</p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {/* General Description */}
                        {kundliData.faladesh.overall_life.description && (
                            <div className="bg-white/[0.08] rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20">
                                <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-primary-500/20 flex items-center justify-center text-primary-400">
                                        <BookOpen size={16} className="md:w-5 md:h-5" />
                                    </div>
                                    <h3 className="text-lg font-bold text-white">{t.generalDesc}</h3>
                                </div>
                                <p className="text-primary-200/70 text-sm md:text-base leading-relaxed">{kundliData.faladesh.overall_life.description}</p>
                            </div>
                        )}

                        {/* Personality Traits */}
                        {kundliData.faladesh.overall_life.personality && (
                            <div className="bg-white/[0.08] rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20">
                                <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                                        <User size={16} className="md:w-5 md:h-5" />
                                    </div>
                                    <h3 className="text-lg font-bold text-white">{t.personalityTraits}</h3>
                                </div>
                                <p className="text-primary-200/70 text-sm md:text-base leading-relaxed">{kundliData.faladesh.overall_life.personality}</p>
                            </div>
                        )}

                        {/* Physical Appearance */}
                        {kundliData.faladesh.overall_life.physical && (
                            <div className="bg-white/[0.08] rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20">
                                <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-pink-500/20 flex items-center justify-center text-pink-400">
                                        <Sparkles size={16} className="md:w-5 md:h-5" />
                                    </div>
                                    <h3 className="text-lg font-bold text-white">{t.physicalAppearance}</h3>
                                </div>
                                <p className="text-primary-200/70 text-sm md:text-base leading-relaxed">{kundliData.faladesh.overall_life.physical}</p>
                            </div>
                        )}

                        {/* Health Insights */}
                        {kundliData.faladesh.overall_life.health && (
                            <div className="bg-white/[0.08] rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20">
                                <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                                        <Droplets size={16} className="md:w-5 md:h-5" />
                                    </div>
                                    <h3 className="text-lg font-bold text-white">{t.healthInsights}</h3>
                                </div>
                                <p className="text-primary-200/70 text-sm md:text-base leading-relaxed">{kundliData.faladesh.overall_life.health}</p>
                            </div>
                        )}

                        {/* Career Prospects */}
                        {kundliData.faladesh.overall_life.career && (
                            <div className="bg-white/[0.08] rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20">
                                <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-amber-500/20 flex items-center justify-center text-amber-400">
                                        <Briefcase size={16} className="md:w-5 md:h-5" />
                                    </div>
                                    <h3 className="text-lg font-bold text-white">{t.careerProspects}</h3>
                                </div>
                                <p className="text-primary-200/70 text-sm md:text-base leading-relaxed">{kundliData.faladesh.overall_life.career}</p>
                            </div>
                        )}

                        {/* Relationships */}
                        {kundliData.faladesh.overall_life.relationship && (
                            <div className="bg-white/[0.08] rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20">
                                <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-red-500/20 flex items-center justify-center text-red-400">
                                        <Heart size={16} className="md:w-5 md:h-5" />
                                    </div>
                                    <h3 className="text-lg font-bold text-white">{t.relationships}</h3>
                                </div>
                                <p className="text-primary-200/70 text-sm md:text-base leading-relaxed">{kundliData.faladesh.overall_life.relationship}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Planetary Effects in D1 Chart */}
            {kundliData.faladesh?.planet_d1 && Object.keys(kundliData.faladesh.planet_d1).length > 0 && (
                <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                    <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8">
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-indigo-600 to-primary-600 rounded-xl md:rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-900/30 shrink-0">
                            <Star size={20} className="md:w-7 md:h-7" />
                        </div>
                        <div>
                            <h2 className="text-xl md:text-3xl font-black text-white">{t.planetEffectsTitle}</h2>
                            <p className="text-primary-200/50 text-[11px] md:text-sm font-light italic mt-1 leading-tight">{t.planetEffectsDesc}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(kundliData.faladesh.planet_d1).map(([planet, data]: [string, any]) => (
                            <div key={planet} className="bg-white/[0.08] rounded-2xl p-5 border border-white/20 hover:border-primary-500/30 transition-colors">
                                <h4 className="text-lg font-bold text-white mb-2 capitalize flex items-center gap-2">
                                    <Star size={16} className="text-primary-400" />
                                    {getPlanetName(planet, t.planetNames)}
                                </h4>
                                <p className="text-primary-200/60 text-sm leading-relaxed">
                                    {typeof data === 'object' ? (data.description || data.english || JSON.stringify(data)) : data}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Maha Dasha Predictions */}
            {kundliData.faladesh?.maha_dasha && Object.keys(kundliData.faladesh.maha_dasha).length > 0 && (
                <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                    <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8">
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-amber-600 to-orange-600 rounded-xl md:rounded-2xl flex items-center justify-center text-white shadow-lg shadow-amber-900/30 shrink-0">
                            <Clock size={20} className="md:w-7 md:h-7" />
                        </div>
                        <div>
                            <h2 className="text-xl md:text-3xl font-black text-white">{t.mahaDashaTitle}</h2>
                            <p className="text-primary-200/50 text-[11px] md:text-sm font-light italic mt-1 leading-tight">{t.mahaDashaDesc}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        {Object.entries(kundliData.faladesh.maha_dasha).map(([planet, data]: [string, any]) => (
                            <div key={planet} className="bg-white/[0.08] rounded-2xl p-5 border border-white/20 hover:border-primary-500/30 transition-colors">
                                <div className="flex items-start justify-between mb-3">
                                    <h4 className="text-lg font-bold text-white capitalize flex items-center gap-2">
                                        <Clock size={16} className="text-amber-400" />
                                        {getPlanetName(planet, t.planetNames)} {t.mahadashaSuffix}
                                    </h4>
                                    {data?.period && (
                                        <span className="text-xs text-primary-400 bg-primary-500/10 px-3 py-1 rounded-full font-mono">
                                            {data.period}
                                        </span>
                                    )}
                                </div>
                                <p className="text-primary-200/60 text-sm leading-relaxed">
                                    {typeof data === 'object' ? (data.description || data.english || JSON.stringify(data)) : data}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PredictionsTab;
